import { motion } from 'motion/react';
import styled from 'styled-components';

import breakpoints, { under } from '@polyai/ui/styles/config/breakpoints';
import themeColors from '@polyai/ui/styles/config/colors';
import themeSpacing from '@polyai/ui/styles/config/spacing';
import text from '@polyai/ui/styles/config/text';

import PolyaiLogoSlate from 'assets/svg/companyLogos/polyai-logo-slate-rgb.svg';
import PolyaiLogoWhite from 'assets/svg/companyLogos/polyai-logo-white-rgb.svg';

export { ContentContainer } from 'screens/AgentWizard/AgentWizard.styled';

export const RightPanel = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  padding: ${themeSpacing.sm3} ${themeSpacing.md3} ${themeSpacing.md3};

  background: ${themeColors.background};

  @media ${under(breakpoints.sm)} {
    padding: ${themeSpacing.sm3} ${themeSpacing.sm2} ${themeSpacing.sm2};
  }
`;

export const Content = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;
`;

export const ScrollableSection = styled.div`
  overflow-y: auto;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${themeSpacing.sm2};

  min-height: 0;
  margin-bottom: ${themeSpacing.sm2};
  padding-right: ${themeSpacing.xs2};
`;

export const FixedBottomSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: ${themeSpacing.sm2};

  padding-bottom: ${themeSpacing.sm1};

  background: ${themeColors.background};
`;

export const Title = styled.h2`
  margin: 0 0 ${themeSpacing.sm3};
  font-size: ${text.size.header.large};
  font-weight: ${text.weight.semibold};
  color: ${themeColors.textPrimary};
`;

export const Question = styled.div`
  margin-bottom: ${themeSpacing.sm2};
`;

export const QuestionLabel = styled.div`
  margin-bottom: ${themeSpacing.xs3};
  font-size: ${text.size.body.regular};
  font-weight: ${text.weight.medium};
  color: ${themeColors.textPrimary};
`;

export const OptionsGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${p => p.$columns || 4},
    1fr
  );
  gap: ${themeSpacing.xs3};

  @media ${under(breakpoints.md)} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${under(breakpoints.sm)} {
    grid-template-columns: 1fr;
  }
`;

export const OptionButton = styled.button<{ $selected?: boolean }>`
  cursor: pointer;

  display: flex;
  gap: ${themeSpacing.xs2};
  align-items: center;
  justify-content: center;

  padding: ${themeSpacing.xs3} ${themeSpacing.sm1};

  font-size: ${text.size.body.small};
  font-weight: ${text.weight.medium};
  color: ${p => (p.$selected ? themeColors.textPrimaryInverse : themeColors.textSecondary)};

  background: ${p => (p.$selected ? themeColors.backgroundPrimaryHover : themeColors.background)};
  border: 1px solid
    ${p => (p.$selected ? themeColors.borderStrong : themeColors.borderSubtle)};
  border-radius: ${themeSpacing.xs3};

  transition: all 0.2s ease;

  &:hover {
    background: ${p =>
      p.$selected
        ? themeColors.backgroundPrimaryHover
        : themeColors.backgroundHover};
    border-color: ${themeColors.borderStrong};
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

export const Logo = styled(PolyaiLogoWhite)`
  position: absolute;
  top: ${themeSpacing.sm3};
  left: ${themeSpacing.sm3};
  transform: translateZ(0);

  display: block;

  width: auto;
  height: ${themeSpacing.sm4};

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricprecision;

  shape-rendering: geometricprecision;
`;

export const MobileLogo = styled(PolyaiLogoSlate)`
  position: absolute;
  top: ${themeSpacing.sm3};
  left: ${themeSpacing.sm3};
  transform: translateZ(0);

  display: none;

  width: auto;
  height: ${themeSpacing.sm4};

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricprecision;

  shape-rendering: geometricprecision;

  @media ${under(breakpoints.sm)} {
    display: none; /* Hide on small screens to avoid overlap with progress slider */
  }
`;

export const BackButton = styled.button`
  cursor: pointer;

  position: absolute;
  top: ${themeSpacing.sm3};
  left: ${themeSpacing.sm3};

  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;

  color: ${themeColors.textSecondary};

  background: transparent;
  border: none;
  border-radius: 50%;

  transition: all 0.2s ease;

  &:hover {
    color: ${themeColors.textPrimary};
    background: ${themeColors.backgroundHover};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media ${under(breakpoints.sm)} {
    top: ${themeSpacing.sm2};
    left: ${themeSpacing.sm2};
  }
`;

export const LoadingContainer = styled.div`
  display: grid;
  gap: ${themeSpacing.xs2};
  place-items: center;
  min-height: 60vh;
`;

export const LoadingText = styled.div`
  font-size: ${text.size.header.large};
  font-weight: ${text.weight.semibold};
  line-height: 1.2;
`;

export const ShimmerText = styled(motion.div)`
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: transparent;

  background: linear-gradient(
    90deg,
    ${themeColors.textShimmerDark},
    ${themeColors.textShimmerMid},
    ${themeColors.textShimmerLight}
  );
  background-clip: text;
  background-size: 200% 100%;
`;
